import type { CourseCertificateEvidenceModel } from "@/api/cle-api";
import EditIcon from "@/components/icons/EditIcon";
import { ModalProvider, ModalTrigger } from "@/components/Modal/Modal";
import { UserQueryContext } from "@/context/UserContext";
import { useContext } from "react";

import { CourseCertificateValidationEditModal } from "./CourseCertificateValidationEditModal";
import { ExpandableText } from "./ExpandableText";
import { ValidationFieldItem } from "./ValidationFieldItem";
import {
  formatProviderAttestationValue,
  getProviderAttestationNote,
  getRelevanceNote,
} from "./utils/aiRelevance";
import { formatCourseDuration } from "./utils/courseDuration";
import { formatComparedWithName } from "./utils/participantNameComparison";
import { formatBoolean, formatDateLong } from "./utils/formatters";
import {
  coursePeriodFieldStatus,
  coursePeriodLabel,
  formatCoursePeriod,
  getCourseCertificateUnmatchedFailingFields,
  getCourseTitleFieldStatus,
  getValidationFieldStatus,
} from "./utils/validationFieldStatus";

interface CourseCertificateValidationProps {
  certificate: CourseCertificateEvidenceModel;
  onCertificateUpdated?: (updated: CourseCertificateEvidenceModel) => void;
}

export const CourseCertificateValidation = ({
  certificate,
  onCertificateUpdated,
}: CourseCertificateValidationProps) => {
  const { user, hasPermission } = useContext(UserQueryContext);
  // Reported (indberettet) evidence backs a signed declaration and is immutable;
  // the API refuses these edits with 409 EVIDENCE_ALREADY_REPORTED.
  const canEdit = hasPermission(user, "TilsynWrite") && !certificate.isReported;
  const fieldStatus = getValidationFieldStatus(certificate);
  const courseTitleStatus = getCourseTitleFieldStatus(
    fieldStatus.courseTitle,
    certificate.courseTitle,
  );
  const unmatchedFailingFields =
    getCourseCertificateUnmatchedFailingFields(certificate);

  // AI'ens begrundelser er skrivebeskyttede: de er grundlaget for en rettelse,
  // ikke selv noget, der rettes. null på manuelle beviser og ældre analyser.
  const relevanceNote = getRelevanceNote(certificate);
  const attestationNote = getProviderAttestationNote(certificate);

  const handleSave = (updated: CourseCertificateEvidenceModel) => {
    onCertificateUpdated?.(updated);
  };

  return (
    <ModalProvider>
      <div className="c-card ">
        <div className="flex-100 d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Validering</h3>
          {canEdit && (
            <ModalTrigger className="btn btn-primary btn--transparent has-icon px-2 edit-trigger">
              Redigér
              <EditIcon height={16} />
            </ModalTrigger>
          )}
        </div>
        <div className="c-item-list">
          <ValidationFieldItem
            isValid={fieldStatus.isLegalCourseCertificate.approved}
            label="Er det et juridisk kursusbevis?"
            aiNote={relevanceNote}
            reason={fieldStatus.isLegalCourseCertificate.reason}
            value={formatBoolean(certificate.isLegalCourseCertificate)}
          />
          <ValidationFieldItem
            isValid={courseTitleStatus.approved}
            label="Kursustitel"
            reason={courseTitleStatus.reason}
            value={certificate.courseTitle ?? "-"}
          />
          <ValidationFieldItem
            isValid={fieldStatus.courseProvider.approved}
            label="Kursusudbyder"
            reason={fieldStatus.courseProvider.reason}
            value={certificate.courseProvider ?? "-"}
          />
          {/* Navnet valideres mod ejerens navn ved upload, ikke det nuvaerende.
              Uden den linje her laeser en afvisning som en selvmodsigelse, naar
              advokaten har skiftet navn siden: personsiden viser et navn der
              matcher, mens beskeden siger det modsatte. */}
          <ValidationFieldItem
            hint={formatComparedWithName(certificate.ownerDisplayNameSnapshot)}
            isValid={fieldStatus.participantName.approved}
            label="Deltagernavn"
            reason={fieldStatus.participantName.reason}
            value={certificate.participantName ?? "-"}
          />
          <ValidationFieldItem
            isValid={coursePeriodFieldStatus(fieldStatus).approved}
            label={coursePeriodLabel(certificate)}
            reason={coursePeriodFieldStatus(fieldStatus).reason}
            value={formatCoursePeriod(certificate, formatDateLong)}
          />
          <ValidationFieldItem
            isValid={fieldStatus.courseTime.approved}
            label="Kursets varighed"
            reason={fieldStatus.courseTime.reason}
            value={formatCourseDuration(certificate.courseTime)}
          />
          <ValidationFieldItem
            isValid={fieldStatus.numberOfLessons.approved}
            label="Antal lektioner"
            reason={fieldStatus.numberOfLessons.reason}
            value={certificate.numberOfLessons?.toString() ?? "-"}
          />
          {/* Kriterie 3: underskrift ELLER udbyderens logo. Ét krav, én række —
              API'et rapporterer begge under nøglen `signature`. */}
          <ValidationFieldItem
            isValid={fieldStatus.signature.approved}
            label="Underskrift eller udbyders logo"
            aiNote={attestationNote}
            reason={fieldStatus.signature.reason}
            value={formatProviderAttestationValue(
              certificate.signature,
              certificate.providerLogo,
            )}
          />
          {/* Fields the API rejected that this card has no row for — without
              these the certificate looks fully approved while its status says
              rejected. */}
          {unmatchedFailingFields.map((field) => (
            <ValidationFieldItem
              key={field.key}
              isValid={false}
              label={field.label}
              reason={field.reason}
              value="-"
            />
          ))}
          <ValidationFieldItem
            className="flex-100"
            isValid={fieldStatus.courseDescription.approved}
            label="Kursusbeskrivelse"
            reason={fieldStatus.courseDescription.reason}
            value={
              <ExpandableText
                id="course-certificate-description-toggle"
                text={certificate.courseDescription}
              />
            }
          />
        </div>
      </div>
      {canEdit && (
        <CourseCertificateValidationEditModal
          certificate={certificate}
          onSave={handleSave}
        />
      )}
    </ModalProvider>
  );
};
